/**
 * Query-param builder kit — shared filter→backend-param composition for
 * server-backed grids and report views.
 *
 * Grid and report surfaces across the app turn UI filter state into the
 * backend query params their endpoint expects, then merge that with
 * pagination/sort. The same four ingredients recur: scope ids
 * (customer/site group), date-range serialization, boolean filter flags,
 * and a final shallow merge. This kit owns those pure, reusable pieces so
 * per-resource builders can compose them instead of re-implementing
 * serialization and merge; it deliberately does NOT own param *names* —
 * those are per-endpoint contract facts and stay in the feature-local
 * builder (see the contacts/maintenance/curfew/radio builders).
 *
 * All functions are pure and return new objects (immutability rule).
 *
 * Contains:
 * - serializeDateOnly — Date → UTC `YYYY-MM-DD`
 * - withScope — merge in `customer_id` / `site_group_id` when present
 * - withDateRange — merge in a serialized start/end pair under given keys
 * - withFlags — merge in truthy boolean flags as `true`
 * - buildQueryParams — shallow-merge any number of partial param objects
 */

/** Serialize a Date to a UTC calendar day (`YYYY-MM-DD`). */
export const serializeDateOnly = date => date.toISOString().split('T')[0];

/**
 * Merge customer/site-group scope ids into a params object. Each id is
 * added only when truthy and passed through verbatim (no coercion) — the
 * three grid pilots all use the `*_id`-suffixed scope contract.
 *
 * @param {Object} params - base params to extend
 * @param {Object} [scope]
 * @param {number|string} [scope.customerId]
 * @param {number|string} [scope.siteGroupId]
 * @returns {Object} new params object
 */
export const withScope = (params, { customerId, siteGroupId } = {}) => {
  const next = { ...params };
  if (customerId) next.customer_id = customerId;
  if (siteGroupId) next.site_group_id = siteGroupId;
  return next;
};

/**
 * Merge a serialized start/end date pair into a params object under the
 * given keys. `serialize` is injectable because endpoints disagree on the
 * calendar-day convention (UTC `toISOString` vs a local `date-fns` format);
 * it defaults to UTC `serializeDateOnly`.
 *
 * @param {Object} params - base params to extend
 * @param {Object} [range]
 * @param {Date} [range.start]
 * @param {Date} [range.end]
 * @param {string} [range.startKey='date_start']
 * @param {string} [range.endKey='date_end']
 * @param {(date: Date) => string} [range.serialize=serializeDateOnly]
 * @returns {Object} new params object
 */
export const withDateRange = (
  params,
  { start, end, startKey = 'date_start', endKey = 'date_end', serialize = serializeDateOnly } = {},
) => {
  const next = { ...params };
  if (start != null) next[startKey] = serialize(start);
  if (end != null) next[endKey] = serialize(end);
  return next;
};

/**
 * Merge boolean filter flags into a params object, keeping only the truthy
 * ones (emitted as `true`) so absent/false flags never reach the wire.
 *
 * @param {Object} params - base params to extend
 * @param {Object<string, boolean>} [flags]
 * @returns {Object} new params object
 */
export const withFlags = (params, flags = {}) => {
  const next = { ...params };
  Object.entries(flags).forEach(([key, value]) => {
    if (value) next[key] = true;
  });
  return next;
};

/**
 * Shallow-merge any number of partial param objects, left to right.
 *
 * @param {...Object} parts
 * @returns {Object} new merged params object
 */
export const buildQueryParams = (...parts) => parts.reduce((acc, part) => ({ ...acc, ...part }), {});
